import { ArrowLeftIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';
import { visibleAreas } from '../../config/areas';
import { useAuth } from '../../hooks/useAuth';
import { useCountryLanguageDefault } from '../../hooks/useCountryLanguageDefault';
import { RequireRole } from '../auth/RequireRole';
import type { AreaContext } from './AppShell';
import { ManagementSidebar } from './ManagementSidebar';
import { UserMenu } from './UserMenu';

interface ManagementShellProps {
  requiredRoles: readonly string[];
}

/**
 * The management experience: Template Studio and Administration, entered only via the
 * end-user shell's cog. A left sidebar (the standard admin-console pattern) replaces a
 * top dropdown, with an explicit way back to the Documents experience.
 */
export function ManagementShell({ requiredRoles }: ManagementShellProps) {
  const { t } = useTranslation();
  const { user, roles, loading } = useAuth();
  const { pathname } = useLocation();
  const areas = visibleAreas(roles).filter((area) => area.key === 'studio' || area.key === 'settings');
  const [sidebar, setSidebar] = useState({ open: false, path: pathname });
  // Derived rather than closed from an effect: the panel belongs to the route it was
  // opened on, so any navigation implicitly closes it.
  const sidebarOpen = sidebar.open && sidebar.path === pathname;

  useCountryLanguageDefault();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-surface">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-3 sm:gap-4 sm:px-6 lg:px-8">
          <button
            type="button"
            aria-label={t('nav.openMenu')}
            aria-haspopup="true"
            aria-expanded={sidebarOpen}
            aria-controls="management-sidebar-mobile"
            onClick={() => setSidebar({ open: true, path: pathname })}
            className="inline-flex shrink-0 items-center justify-center rounded border border-input bg-surface p-1.5 text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
          >
            <Bars3Icon className="size-5" aria-hidden="true" />
          </button>

          <Link
            to="/app"
            className="inline-flex shrink-0 items-center gap-1.5 font-heading font-semibold whitespace-nowrap text-foreground transition hover:text-muted-foreground"
          >
            <ArrowLeftIcon className="size-4" aria-hidden="true" />
            {t('nav.backToApp')}
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <UserMenu user={user} roles={roles} />
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-7xl items-start gap-6 px-5 py-6 sm:px-6 lg:px-8">
        <ManagementSidebar
          areas={areas}
          roles={roles}
          open={sidebarOpen}
          onClose={() => setSidebar((prev) => ({ ...prev, open: false }))}
        />

        <main className="min-w-0 flex-1">
          <RequireRole anyOf={requiredRoles} roles={roles} loading={loading}>
            <Outlet context={{ roles } satisfies AreaContext} />
          </RequireRole>
        </main>
      </div>
    </div>
  );
}
